import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Col } from 'reactstrap';
import { Button, JustifyContents, Alignment, Icon, SearchInput, Variant, DropButton } from 'tacho-ui';
import { LicenseModel } from '../../../../core/domainModels/licences/LicenseModel';
import { ApplicationState } from '../../../../state/ApplicationState';
import { TachoToolbar, TachoToolbarHorizontalLine } from '../../../components/TachoToolbar';
import { LicenseStatusEnum } from '../../../../core/domainModels/licences/LicenseStatus';

interface LicencesToolbarProps {
    selectedLicences: Array<LicenseModel>;
    openConfirmRemoveModal: () => void;
    openGenerateLicenceModal: () => void;
    openAddLicenceVersionModal: () => void;
    openCarLimitModal: () => void;
    onRefresh: () => void;
    onSearchQueryChanged: (newQuery: string) => void;
    openUpdateLicenceModal: () => void;
    changeLicenceStatus: (status: boolean) => void;
    downloadLicence: () => void;
    sendLicenceViaEmailToClient: () => void;
    allowCarStatusCounterReset: () => void;
}
export const LicencesToolbar = (props: LicencesToolbarProps) => {
    const { formatMessage } = useIntl();
    const [typingTimeout, setTypingTimeout] = useState(0);
    const generateLicencePending = useSelector((state: ApplicationState) => state.licences.generateLicencePending);
    const updateLicencePending = useSelector((state: ApplicationState) => state.licences.updateLicencePending);
    const licenceVersionPending = useSelector((state: ApplicationState) => state.licences.licenceVersionPending);
    const allowCarStatusCounterResetPending = useSelector(
        (state: ApplicationState) => state.licences.allowCarStatusCounterResetPending,
    );

    const searchQueryTypingTimeoutMS = 500;
    function onSearchInputChanged(newValue: string): void {
        setTypingTimeout(Date.now() + searchQueryTypingTimeoutMS);
        const timeoutDelayMS = 10;
        setTimeout(() => {
            const now = Date.now();
            if (now > typingTimeout) {
                props.onSearchQueryChanged(newValue);
            }
        }, searchQueryTypingTimeoutMS + timeoutDelayMS);
    }
    const statusOptions = useMemo(
        () => [
            {
                label: formatMessage({ id: 'Active' }),
                onItemClick: () => props.changeLicenceStatus(true),
                iconName: Icon.Unlock,
                disabled: props.selectedLicences[0] && props.selectedLicences[0].status === LicenseStatusEnum.Active,
            },
            {
                label: formatMessage({ id: 'Blocked' }),
                onItemClick: () => props.changeLicenceStatus(false),
                iconName: Icon.Lock,
                disabled: props.selectedLicences[0] && props.selectedLicences[0].status === LicenseStatusEnum.Blocked,
            },
        ],
        [props.selectedLicences],
    );
    const addLicenceOptions = useMemo(
        () => [
            {
                label: formatMessage({ id: 'licencesAddButton' }),
                onItemClick: () => props.openGenerateLicenceModal(),
                iconName: Icon.Add,
            },
            {
                label: formatMessage({ id: 'licencesVersionAddButton' }),
                onItemClick: () => props.openAddLicenceVersionModal(),
                iconName: Icon.Add,
                disabled: !props.selectedLicences[0] || !props.selectedLicences[0].licenseVersions,
            },
        ],
        [props.selectedLicences],
    );

    const isNotLicence = props.selectedLicences.length !== 1 || !props.selectedLicences[0].licenseVersions;
    return (
        <TachoToolbar>
            <Col className="d-flex flex-wrap gap-2">
                <DropButton
                    array={addLicenceOptions}
                    title={formatMessage({ id: 'commonAdd' })}
                    icon={Icon.Add}
                    className="primary"
                    loading={generateLicencePending || licenceVersionPending}
                    disabled={generateLicencePending || licenceVersionPending}
                />

                <Button
                    justify={JustifyContents.center}
                    align={Alignment.center}
                    gap={4}
                    text={formatMessage({ id: 'commonEdit' })}
                    icon={Icon.Edit}
                    className="primary"
                    onClick={props.openUpdateLicenceModal}
                    loading={updateLicencePending || licenceVersionPending}
                    disabled={
                        props.selectedLicences.length !== 1 ||
                        props.selectedLicences[0].licenseVersions.length === 0 ||
                        updateLicencePending
                    }
                />
                <Button
                    justify={JustifyContents.center}
                    align={Alignment.center}
                    gap={4}
                    text={formatMessage({ id: 'commonDelete' })}
                    icon={Icon.Delete}
                    className="primary"
                    onClick={props.openConfirmRemoveModal}
                    loading={licenceVersionPending}
                    disabled={isNotLicence || licenceVersionPending}
                />
                <DropButton
                    array={statusOptions}
                    title={formatMessage({ id: 'licenceStatusChange' })}
                    icon={Icon.Edit}
                    className="primary"
                    loading={updateLicencePending || licenceVersionPending}
                    disabled={isNotLicence || updateLicencePending || licenceVersionPending}
                />
                <Button
                    justify={JustifyContents.center}
                    align={Alignment.center}
                    gap={4}
                    text={formatMessage({ id: 'licenceAllowCarStatusCounterReset' })}
                    icon={Icon.Restore}
                    className="primary"
                    onClick={props.openCarLimitModal}
                    disabled={
                        (props.selectedLicences[0] && props.selectedLicences[0].canCarStatusCounterBeResetOnce) ||
                        isNotLicence ||
                        allowCarStatusCounterResetPending
                    }
                    loading={allowCarStatusCounterResetPending}
                />
                <TachoToolbarHorizontalLine />
                <Button
                    justify={JustifyContents.center}
                    align={Alignment.center}
                    gap={4}
                    text={formatMessage({ id: 'licenceSend' })}
                    icon={Icon.DocumentAdd}
                    className="primary"
                    onClick={props.sendLicenceViaEmailToClient}
                    loading={allowCarStatusCounterResetPending}
                    disabled={isNotLicence || allowCarStatusCounterResetPending}
                />
                <Button
                    justify={JustifyContents.center}
                    align={Alignment.center}
                    gap={4}
                    text={formatMessage({ id: 'licenceDownload' })}
                    icon={Icon.Download}
                    className="primary"
                    onClick={props.downloadLicence}
                    disabled={isNotLicence}
                />

                <TachoToolbarHorizontalLine />

                <SearchInput
                    onChange={onSearchInputChanged}
                    width="auto"
                    height="auto"
                    variant={Variant.Toolbar}
                    placeholder={formatMessage({ id: 'commonSearch' })}
                    value={undefined}
                />

                <Button
                    justify={JustifyContents.center}
                    align={Alignment.center}
                    gap={4}
                    icon={Icon.Refresh}
                    className="primary"
                    onClick={props.onRefresh}
                />
            </Col>
        </TachoToolbar>
    );
};
